import { PinnedLibrary } from "interfaces";
import type { ClientLibrary } from "pages/api/libraries";
import { getStoredItem, setStoredItem } from "utils/browserStorage";

export const PINNED_LIBRARIES_KEY = "CPW_PINNED_LIBRARIES";

function isPinnedLibrary(value: unknown): value is PinnedLibrary {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === "string" &&
    typeof v.slug === "string" &&
    typeof v.title === "string" &&
    (v.logoUrl === undefined || typeof v.logoUrl === "string") &&
    Number.isFinite(v.pinnedAt)
  );
}

/**
 * Reads the pinned library list from localStorage. Unavailable storage and
 * malformed JSON yield an empty list; entries missing required fields are
 * dropped.
 */
export function readPinnedLibraries(): PinnedLibrary[] {
  const raw = getStoredItem(PINNED_LIBRARIES_KEY);
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isPinnedLibrary) : [];
  } catch {
    return [];
  }
}

/** Returns false when storage is unavailable or full. */
export function writePinnedLibraries(libraries: PinnedLibrary[]): boolean {
  return setStoredItem(PINNED_LIBRARIES_KEY, JSON.stringify(libraries));
}

/**
 * Returns `libraries` with `library` added. Re-pinning an already-pinned id
 * replaces its fields but keeps the original pinnedAt. Replacement is
 * deliberate: omitting an optional field (such as logoUrl) clears it.
 */
export function withPinned(
  libraries: PinnedLibrary[],
  library: Omit<PinnedLibrary, "pinnedAt">
): PinnedLibrary[] {
  const existing = libraries.find(lib => lib.id === library.id);
  if (existing) {
    return libraries.map(lib =>
      lib.id === library.id ? { ...library, pinnedAt: existing.pinnedAt } : lib
    );
  }
  return [...libraries, { ...library, pinnedAt: Date.now() }];
}

/**
 * Returns `libraries` without the entry whose id matches, or the original
 * array when the id is not pinned.
 */
export function withoutPinned(
  libraries: PinnedLibrary[],
  id: string
): PinnedLibrary[] {
  return libraries.some(lib => lib.id === id)
    ? libraries.filter(lib => lib.id !== id)
    : libraries;
}

/**
 * Returns `libraries` with the entry whose id is `id` moved to the position
 * of the entry whose id is `targetId`, shifting the entries in between.
 * Returns the original array when either id is not pinned or they match.
 */
export function withMovedTo(
  libraries: PinnedLibrary[],
  id: string,
  targetId: string
): PinnedLibrary[] {
  const from = libraries.findIndex(lib => lib.id === id);
  const to = libraries.findIndex(lib => lib.id === targetId);
  if (from === -1 || to === -1 || from === to) return libraries;
  const next = [...libraries];
  const [entry] = next.splice(from, 1);
  next.splice(to, 0, entry);
  return next;
}

/**
 * Query parameter naming pinned libraries on the home page, by stable id
 * or by slug, so a bookmarked or shared link can populate My Libraries on
 * another device or in a private window (which cannot see this browser's
 * stored pins).
 */
export const PINS_QUERY_PARAM = "pins";

/**
 * The home page path with a `?pins=` value naming `libraries` by their
 * stable ids, which survive slug changes, in the given order. Entries are
 * comma delimited, so an id containing a comma cannot round-trip.
 */
export function buildPinsPath(libraries: Pick<PinnedLibrary, "id">[]): string {
  const ids = libraries.map(lib => encodeURIComponent(lib.id));
  return `/?${PINS_QUERY_PARAM}=${ids.join(",")}`;
}

/**
 * Returns `pinned` with slug and title refreshed from `available`, matched
 * by id, and a missing logo backfilled when the server list carries one (a
 * stored logo is kept as is). Entries absent from `available` are kept
 * unchanged. Returns the original array when nothing changed.
 */
export function syncPinned(
  pinned: PinnedLibrary[],
  available: ClientLibrary[]
): PinnedLibrary[] {
  const byId = new Map(available.map(lib => [lib.id, lib]));
  let changed = false;
  const next = pinned.map(entry => {
    const current = byId.get(entry.id);
    if (!current) return entry;
    const logoUrl = entry.logoUrl ?? current.logoUrl;
    if (
      current.slug === entry.slug &&
      current.title === entry.title &&
      logoUrl === entry.logoUrl
    ) {
      return entry;
    }
    changed = true;
    return {
      ...entry,
      slug: current.slug,
      title: current.title,
      ...(logoUrl && { logoUrl })
    };
  });
  return changed ? next : pinned;
}
